import type {CSSProperties} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import {InputWrapper} from './TextInputWrapper';

const TextAreaInput = styled(
  'textarea',
  (
    {
      $resize = 'vertical',
      rows,
    }: {
      $resize?: 'both' | 'horizontal' | 'vertical' | 'none';
      rows?: number;
    },
    designColors,
  ) => ({
    appearance: 'none',
    backgroundColor: 'transparent',
    border: 0,
    borderRadius: 4,
    color: designColors.contentText,
    fontFamily: DEFAULT_FONT,
    fontSize: 14,
    height: '100%',
    lineHeight: '20px',
    minHeight: rows ? undefined : 120,
    padding: '8px 10px',
    resize: $resize,
    width: '100%',

    ':focus': {
      outline: 'none',
    },

    '::placeholder': {color: '#c6caca'},
    '::-webkit-placeholder': {color: '#c6caca'},
  }),
  {className: 'TextArea'},
);

type Props = {
  disabled?: boolean;
  invalid?: boolean;
  maxLength?: number;
  minLength?: number;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLTextAreaElement>['onBlur'];
    onChange: React.HTMLAttributes<HTMLTextAreaElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLTextAreaElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLTextAreaElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLTextAreaElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLTextAreaElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLTextAreaElement>['onKeyPress'];
  }>;
  onChange?: (value: string) => void;
  onRef?: React.Ref<HTMLTextAreaElement | null>;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  resize?: 'both' | 'horizontal' | 'vertical' | 'none';
  rows?: number;
  style?: CSSProperties;
  tabIndex?: number;
  value: string;
};

const TextArea = (
  {
    disabled,
    invalid,
    nativeEvents,
    onChange,
    resize,
    style,
    value,
    ...rest
  }: Props,
  ref: React.Ref<HTMLTextAreaElement>,
) => {
  const nativeOnChange = nativeEvents?.onChange;
  const handleChange = React.useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      e.preventDefault();

      onChange?.((e.target as HTMLTextAreaElement).value);
      nativeOnChange?.(e);
    },
    [onChange, nativeOnChange],
  );
  return (
    <InputWrapper
      $disabled={!!disabled}
      $invalid={!!invalid}
      style={{height: 'auto', ...style}}
    >
      <TextAreaInput
        $resize={resize}
        disabled={disabled}
        {...rest}
        dir="auto"
        {...nativeEvents}
        onChange={handleChange}
        ref={ref}
        value={value}
      />
    </InputWrapper>
  );
};

export default React.forwardRef<HTMLTextAreaElement, Props>(TextArea);
